import { action, computed, makeObservable, observable, reaction, type IReactionDisposer } from 'mobx';

import type { TrajectoryService } from '../../service/trajectory-service';
import type { TrajectoryUiStore } from '../../trajectory-ui.store';
import type { ReadonlyDeep } from '../../types/readonly-deep';
import type { TTrajectory } from '../../types/trajectory';

export class TrajectoryListSidebarStore {
  @observable previewTrajectoryId: number | null = null;

  private readonly trajectoryService: TrajectoryService;
  private readonly uiStore: TrajectoryUiStore;
  private disposer: IReactionDisposer | null = null;

  constructor(trajectoryService: TrajectoryService, uiStore: TrajectoryUiStore) {
    this.trajectoryService = trajectoryService;
    this.uiStore = uiStore;
    makeObservable(this);
  }

  @computed
  get trajectories(): ReadonlyDeep<TTrajectory[]> {
    return this.trajectoryService.wellboreTrajectories;
  }

  @computed
  get isLoading(): boolean {
    return this.trajectoryService.isWellboreTrajectoriesLoading;
  }

  @computed
  get previewTrajectory(): ReadonlyDeep<TTrajectory> | null {
    return this.trajectoryService.getTrajectoryById(this.previewTrajectoryId);
  }

  @action.bound
  setPreviewTrajectoryId(trajectoryId: number | null): void {
    this.previewTrajectoryId = trajectoryId;
  }

  @action.bound
  close(): void {
    this.previewTrajectoryId = this.trajectoryService.selectedTrajectoryId;
    this.uiStore.setTrajectoryListOpen(false);
  }

  @action.bound
  apply(): void {
    // Edit-mode apply will persist sidebar field changes later; for now just close.
    this.uiStore.setTrajectoryListOpen(false);
  }

  init = (): VoidFunction => {
    this.disposer = reaction(
      () => ({
        open: this.uiStore.isTrajectoryListOpen,
        wellboreId: this.trajectoryService.selectedWellboreId,
        selectedId: this.trajectoryService.selectedTrajectoryId,
      }),
      ({ open, selectedId }) => {
        if (!open) {
          return;
        }

        this.previewTrajectoryId = selectedId;
        void this.trajectoryService.ensureWellboreTrajectoriesLoaded();
      },
      { fireImmediately: true },
    );

    return () => {
      this.dispose();
    };
  };

  @action.bound
  dispose(): void {
    this.disposer?.();
    this.disposer = null;
    this.previewTrajectoryId = null;
  }
}
